/**
 * @file  AssetViewModal.js
 * @description A specific style of action modal tailored to end users and not HR admins. Could be agency specific with iNGAGED default.
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import mammoth from 'mammoth';
import style from './modals.module.scss';
import Spinner from '../spinners/Spinner';
import {
    BROWSER_PREVIEWABLE_FILE_TYPES,
    canRenderWithOfficeViewer,
    getFileCategory,
    getNormalizedFileType,
    getNormalizedFileTypeV2,
} from '../../helpers/FileUtils';
import { getText } from '../../../i18n';

const DocxPreview = ({ url }) => {
    const [html, setHtml] = React.useState('');
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const fetchAndConvert = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch document');
                }

                const arrayBuffer = await response.arrayBuffer();
                const { value: convertedHtml } = await mammoth.convertToHtml({
                    arrayBuffer,
                });

                setHtml(convertedHtml);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAndConvert();
    }, [url]);

    if (loading) {
        return <p>{getText('msg_34c7e76af7f6')}</p>;
    }

    if (error) {
        return (
            <p>
                {getText('msg_fe9d5a920ed1')} {error}
            </p>
        );
    }

    return (
        <div
            style={{ overflowX: 'clip' }}
            className={style.docxPreview}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

/**
 * AssetViewModal component, rendering a styled modal.
 */
const AssetViewModal = ({ onHide, title, text, fileType, download = null }) => {
    const footerRef = useRef(null);
    const deployedEnvName = process.env.REACT_APP_DEPLOYED_ENV_NAME || 'Local';

    useEffect(() => {
        if (footerRef.current) {
            const buttons = footerRef.current.querySelectorAll('.btn');
            let maxWidth = 0;

            buttons.forEach(button => {
                maxWidth = Math.max(maxWidth, button.offsetWidth);
            });

            buttons.forEach(button => {
                button.style.width = `${maxWidth}px`;
            });
        }
    }, []);

    const normalizedTypeFromFileType = getNormalizedFileType(fileType);
    const normalizedTypeFromUrl = getNormalizedFileType(text);
    const normalizedTypeFromFileTypeV2 = getNormalizedFileTypeV2(fileType);
    const normalizedTypeFromUrlV2 = getNormalizedFileTypeV2(text);
    const fileExtension =
        normalizedTypeFromFileType ||
        normalizedTypeFromUrl ||
        normalizedTypeFromFileTypeV2 ||
        normalizedTypeFromUrlV2;
    const isVideoFile = getFileCategory(fileExtension) === 'video';
    const imageFileTypes = ['png', 'jpg', 'jpeg', 'gif'];

    function getDownloadFileName() {
        const trimmedTitle = (title || '').trim();
        const extension = fileExtension ? `.${fileExtension}` : '';

        if (trimmedTitle) {
            return trimmedTitle.includes('.')
                ? trimmedTitle
                : `${trimmedTitle}${extension}`;
        }

        const pathSegment = (text || '').split('?')[0].split('/').pop();
        if (pathSegment) {
            return pathSegment;
        }

        return `asset${extension}`;
    }

    const handleDownloadClick = async () => {
        if (typeof download === 'function') {
            download();
            return;
        }

        if (!text) {
            return;
        }

        const fileName = getDownloadFileName();

        try {
            const response = await fetch(text);
            if (!response.ok) {
                throw new Error('Failed to download asset');
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            return;
        } catch (error) {
            console.error('Asset download fallback triggered:', error);
        }

        const link = document.createElement('a');
        link.href = text;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const renderModalHeader = () => (
        <>
            <h5 className={`modal-title ${style.title} ${style.profileTitle}`}>
                {title}
            </h5>
            <button
                type="button"
                className={style.downloadAction}
                onClick={handleDownloadClick}
            >
                <i className="bi bi-cloud-download me-2" />
                {getText('msg_a479c9c34e87')}
            </button>
            <button
                type="button"
                className={`btn-close ${style.dismissBtn}`}
                onClick={onHide}
                aria-label={getText('msg_bbfa773e5a63')}
            />
            <br />
        </>
    );

    const renderFullscreenOverlay = content => (
        <div className={style.fullscreenOverlay}>
            <div className={style.fullscreenContent}>
                <button
                    className={style.fullscreenCloseButton}
                    onClick={onHide}
                    aria-label={getText('msg_bbfa773e5a63')}
                >
                    <i className="bi bi-x"></i>
                </button>
                {content}
            </div>
        </div>
    );

    if (fileExtension === 'docx') {
        return (
            <div className={`${style.container} modal-backdrop`}>
                <div
                    className="modal fade show display-block"
                    tabIndex="-1"
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className={`modal-dialog modal-xl modal-dialog-centered ${style.modalSizing}`}
                        style={{ height: '90vh' }}
                    >
                        <div
                            className={`modal-content ${style.modalContent}`}
                            style={{ height: '100%', overflowY: 'auto' }}
                        >
                            {renderModalHeader()}
                            <DocxPreview url={text} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isVideoFile) {
        const sourceType = normalizedTypeFromFileType
            ? `video/${normalizedTypeFromFileType}`
            : undefined;

        return renderFullscreenOverlay(
            <video
                controls
                autoPlay
                playsInline
                className={style.fullscreenVideo}
            >
                <source src={text} type={sourceType} />
                {getText('msg_ba2e8fc0bcc0')}
            </video>,
        );
    }

    if (imageFileTypes.includes(fileExtension)) {
        return renderFullscreenOverlay(
            <img
                src={text}
                alt={title || 'Asset preview'}
                className={style.fullscreenImage}
            />,
        );
    }

    if (BROWSER_PREVIEWABLE_FILE_TYPES.includes(fileExtension)) {
        // Use DocViewer for supported types
        const doc = [
            {
                uri: text,
                fileType: fileExtension,
                fileName: text.split('/').pop(),
            },
        ];

        return (
            <div className={`${style.container} modal-backdrop`}>
                <div
                    className="modal fade show display-block"
                    tabIndex="-1"
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className={`modal-dialog modal-xl modal-dialog-centered ${style.modalSizing}`}
                        style={{ height: '90vh' }}
                    >
                        <div
                            className={`modal-content ${style.modalContent}`}
                            style={{ height: '100%' }}
                        >
                            {renderModalHeader()}
                            <div className={style.docViewerContainer}>
                                <DocViewer
                                    documents={doc}
                                    pluginRenderers={DocViewerRenderers}
                                    config={{ header: { disableHeader: true } }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (canRenderWithOfficeViewer(fileExtension, deployedEnvName)) {
        // Office viewer URL (must be public)
        const embedUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            text,
        )}`;

        const [loading, setLoading] = React.useState(true);

        return (
            <div className={`${style.container} modal-backdrop`}>
                <div
                    className="modal fade show display-block"
                    tabIndex="-1"
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className={`modal-dialog modal-xl modal-dialog-centered ${style.modalSizing}`}
                        style={{ height: '90vh' }}
                    >
                        <div
                            className={`modal-content ${style.modalContent}`}
                            style={{ height: '100%' }}
                        >
                            {renderModalHeader()}

                            {loading && (
                                <div style={{ alignSelf: 'center' }}>
                                    <Spinner width={50} />
                                </div>
                            )}
                            <iframe
                                title={getText('msg_bcd9b3a33d5f')}
                                src={embedUrl}
                                width="100%"
                                height="100%"
                                onLoad={() => {
                                    setLoading(false);
                                }}
                                style={{ display: 'block' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Fallback for unsupported file types
    return (
        <div className={`${style.container} modal-backdrop`}>
            <div
                className="modal fade show display-block"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div
                    className={`modal-dialog modal-xl modal-dialog-centered ${style.modalSizing}`}
                >
                    <div className={`modal-content ${style.modalContent}`}>
                        {renderModalHeader()}
                        <p>{getText('msg_07fc205566e1')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

AssetViewModal.propTypes = {
    onHide: PropTypes.func.isRequired,
    title: PropTypes.string,
    text: PropTypes.string.isRequired,
    fileType: PropTypes.string,
};

DocxPreview.propTypes = {
    url: PropTypes.string.isRequired,
};

export default AssetViewModal;
